import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import {
  Sparkles, TrendingUp, TrendingDown, Minus, Award, ShieldAlert,
  ClipboardCheck, Users, Coins, Settings2, RefreshCw, Crown, Plus, Loader2, Info,
} from "lucide-react";
import CopyButton from "@/components/CopyButton";
import ContextButton from "@/components/ContextButton";
import FrictionScoreTranslated from "@/components/FrictionScoreTranslated";
import { useLocalContext, describeScope } from "@/lib/moduleContext";
import { useActiveDoctrine, doctrinePayload } from "@/lib/doctrine";
import { useDemoMode } from "@/lib/demoMode";
import { notify, fmtError } from "@/lib/notify";

const PILLAR_ICONS = {
  risques: ShieldAlert,
  conformite: ClipboardCheck,
  rh: Users,
  finance: Coins,
  operations: Settings2,
};

function gradeColor(grade) {
  if (!grade) return "#A1A1AA";
  if (grade.startsWith("A")) return "#1D9E75";
  if (grade.startsWith("B")) return "#378ADD";
  if (grade.startsWith("C")) return "#EF9F27";
  return "#E24B4A";
}

function pillarColor(score) {
  if (score >= 80) return "#1D9E75";
  if (score >= 65) return "#378ADD";
  if (score >= 50) return "#EF9F27";
  return "#E24B4A";
}

function deltaIcon(d) {
  if (d > 0) return <TrendingUp className="w-3.5 h-3.5" />;
  if (d < 0) return <TrendingDown className="w-3.5 h-3.5" />;
  return <Minus className="w-3.5 h-3.5" />;
}

const LOCALE_MAP = { fr: "fr-FR", en: "en-US", de: "de-DE" };

export default function Score() {
  const { t, i18n } = useTranslation();
  const isDemo = useDemoMode();
  const summaryEndpoint = isDemo ? "/score/summary" : "/workspace/score/summary";
  const linkBase = isDemo ? "/demo" : "/app";
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [commentary, setCommentary] = useState(null);
  const [loadingCommentary, setLoadingCommentary] = useState(false);
  const [orgPhase, setOrgPhase] = useState(null);
  const [explainBusy, setExplainBusy] = useState(false);
  const [explanation, setExplanation] = useState(null);

  const explainDelta = async () => {
    setExplainBusy(true);
    try {
      const r = await api.post("/workspace/score/explain-delta");
      setExplanation(r.data);
    } catch (e) {
      notify.error(fmtError(e, "Justification impossible"));
    } finally {
      setExplainBusy(false);
    }
  };
  const [scope] = useLocalContext("score");
  const activeDoctrine = useActiveDoctrine();

  useEffect(() => {
    api.get(summaryEndpoint)
      .then((r) => setData(r.data))
      .catch((e) => setError(e?.response?.data?.detail || t("saas.score.load_error")));
    if (!isDemo) {
      api.get("/workspace/organisation").then((r) => setOrgPhase(r.data?.phase))
        .catch(() => { /* silent — defensive */ });
    }
  }, [summaryEndpoint, isDemo, t]);

  const generateCommentary = async () => {
    setLoadingCommentary(true);
    try {
      const r = await api.post("/score/commentary", {
        phase: orgPhase || undefined,
        context_hint: describeScope(scope),
        doctrine: doctrinePayload(activeDoctrine),
      });
      setCommentary(r.data.commentary);
    } catch (e) {
      setCommentary(t("saas.score.ai_error"));
    } finally {
      setLoadingCommentary(false);
    }
  };

  if (error) return <div className="text-[#E24B4A] text-sm" data-testid="score-error">{error}</div>;
  if (!data) return <div className="text-zinc-400">{t("saas.score.loading")}</div>;

  if (data.is_empty && !isDemo) {
    return (
      <div className="space-y-5" data-testid="score-empty">
        <div>
          <div className="noam-overline">{t("saas.sections.strategic")}</div>
          <h1 className="noam-display text-2xl font-bold tracking-tight">{t("saas.nav.score")}</h1>
          <p className="text-zinc-400 text-sm mt-1">{t("saas.score.subtitle_short")}</p>
        </div>
        <div className="noam-card p-10 text-center">
          <div className="w-14 h-14 mx-auto rounded-xl bg-[#378ADD]/10 border border-[#378ADD]/30 flex items-center justify-center">
            <Award className="w-6 h-6 text-[#378ADD]" />
          </div>
          <h2 className="noam-display text-xl font-semibold mt-4">{t("saas.score.empty_title")}</h2>
          <p className="text-zinc-400 text-sm mt-2 max-w-md mx-auto">
            {t("saas.score.empty_help")}
          </p>
          <div className="mt-6 flex items-center justify-center gap-2 flex-wrap">
            <Link to={`${linkBase}/risques`} className="btn-primary text-sm" data-testid="score-empty-risques">
              <Plus className="w-4 h-4" /> {t("saas.score.empty_create_risk")}
            </Link>
            <Link to="/app/configurer" className="btn-ghost text-sm" data-testid="score-empty-assistant">
              <Sparkles className="w-4 h-4" /> {t("saas.score.empty_assistant")}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const radius = 90;
  const circ = 2 * Math.PI * radius;
  const offset = circ - (data.overall / 100) * circ;
  const gColor = gradeColor(data.grade);

  const hist = data.history.filter((h) => h.score !== null);
  const maxH = Math.max(...hist.map((h) => h.score), 100);
  const minH = Math.min(...hist.map((h) => h.score), 0);
  const span = Math.max(maxH - minH, 1);
  const w = 280, h = 60;
  const pts = hist.map((p, i) => {
    const x = (i / (hist.length - 1 || 1)) * w;
    const y = h - ((p.score - minH) / span) * h;
    return `${x},${y}`;
  }).join(" ");

  const dateStr = new Date().toLocaleString(LOCALE_MAP[i18n.language] || "fr-FR");
  const tPillar = (p, key, fallback) => t(`saas.pillars.${p.key}.${key}`, { defaultValue: fallback });
  const tIndicator = (name) => t(`saas.indicateurs.${name}`, { defaultValue: name });
  const strongestLabel = labelFor(data.pillars, data.highlight.strongest, t);
  const weakestLabel = labelFor(data.pillars, data.highlight.weakest, t);

  return (
    <div className="space-y-6" data-testid="score-root">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-[#378ADD] noam-overline">
            <Crown className="w-3 h-3" /> {t("saas.score.overline_strategic")}
          </div>
          <h1 className="noam-display text-3xl font-bold tracking-tight mt-1">
            {t("saas.score.title")} · {data.period}
          </h1>
          <p className="text-zinc-400 text-sm mt-2 max-w-3xl">
            {t("saas.score.subtitle")}
          </p>
          <div className="text-[10px] text-zinc-500 mt-1">
            {t("saas.score.last_update", { date: dateStr })}
          </div>
        </div>
        {!isDemo && <ContextButton moduleKey="score" />}
      </div>

      {/* V23.15.32 — Doctrine 3Q · "Et alors ?" — Traduction du score friction en CHF/an évitables */}
      {!isDemo && (
        <FrictionScoreTranslated size="md" showCta={true} currency="CHF" data-testid="score-friction-translated" />
      )}

      {/* HEADLINE */}
      <div className="grid lg:grid-cols-3 gap-4">
        <div className="noam-card p-6 lg:col-span-1 flex flex-col items-center text-center" data-testid="score-headline">
          <div className="relative">
            <svg width="220" height="220" viewBox="0 0 220 220">
              <circle cx="110" cy="110" r={radius} fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="10" />
              <circle
                cx="110" cy="110" r={radius} fill="none"
                stroke={gColor} strokeWidth="10" strokeLinecap="round"
                strokeDasharray={circ} strokeDashoffset={offset}
                transform="rotate(-90 110 110)"
                style={{ transition: "stroke-dashoffset 1s ease" }}
              />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <div className="noam-display text-5xl font-bold leading-none" data-testid="score-overall">
                {data.overall}
                <span className="text-xl text-zinc-500">/100</span>
              </div>
              <div
                className="noam-display text-2xl font-bold mt-2 px-3 py-0.5 rounded-md"
                style={{ background: `${gColor}1A`, color: gColor, border: `1px solid ${gColor}55` }}
                data-testid="score-grade"
              >
                {data.grade}
              </div>
            </div>
          </div>

          <div
            className={`mt-3 text-xs flex items-center gap-1.5 ${
              data.delta > 0 ? "text-[#1D9E75]" : data.delta < 0 ? "text-[#E24B4A]" : "text-zinc-400"
            }`}
            data-testid="score-delta"
          >
            {deltaIcon(data.delta)}
            {data.delta > 0 ? "+" : ""}{data.delta} {t("saas.score.delta_vs_prev")}
          </div>

          {data.delta !== 0 && !explanation && (
            <button
              onClick={explainDelta}
              disabled={explainBusy}
              className="btn-ghost text-[11px] mt-2 disabled:opacity-50"
              data-testid="score-explain-btn"
            >
              {explainBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
              {t("saas.score.explain_btn", { defaultValue: "Justifier la variation" })}
            </button>
          )}
          {explanation && (
            <div className="mt-3 p-3 rounded-md border border-[#378ADD]/30 bg-[#378ADD]/5" data-testid="score-explanation">
              <div className="text-[10px] uppercase tracking-wider text-[#378ADD] mb-1.5 flex items-center gap-1">
                <Sparkles className="w-3 h-3" />
                {t("saas.score.explain_overline", { defaultValue: "Cause de la variation" })}
                {explanation.from && explanation.to && (
                  <span className="text-zinc-500 normal-case tracking-normal">
                    · {explanation.from} → {explanation.to}
                  </span>
                )}
              </div>
              <pre className="whitespace-pre-wrap text-[12px] leading-relaxed text-zinc-200 font-sans">
                {explanation.explanation}
              </pre>
              <button
                onClick={explainDelta}
                disabled={explainBusy}
                className="text-[10px] text-zinc-500 hover:text-white mt-2 disabled:opacity-50"
                data-testid="score-explain-regen"
              >
                {explainBusy ? "…" : t("saas.score.explain_regen", { defaultValue: "Régénérer" })}
              </button>
            </div>
          )}
        </div>

        {/* History + IA */}
        <div className="noam-card p-6 lg:col-span-2 flex flex-col">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="noam-overline">{t("saas.score.evolution_overline")}</div>
              <div className="noam-display text-base font-semibold">{t("saas.score.evolution_title")}</div>
            </div>
            <Award className="w-4 h-4 text-[#378ADD]" />
          </div>

          <div className="flex items-center gap-6 flex-1">
            <svg width={w} height={h + 20} className="overflow-visible">
              <defs>
                <linearGradient id="hist-grad" x1="0" x2="0" y1="0" y2="1">
                  <stop offset="0" stopColor="#378ADD" stopOpacity="0.4" />
                  <stop offset="1" stopColor="#378ADD" stopOpacity="0" />
                </linearGradient>
              </defs>
              <polyline points={pts} fill="none" stroke="#378ADD" strokeWidth="2" strokeLinejoin="round" />
              <polyline points={`0,${h} ${pts} ${w},${h}`} fill="url(#hist-grad)" stroke="none" />
              {hist.map((p, i) => {
                const x = (i / (hist.length - 1 || 1)) * w;
                const y = h - ((p.score - minH) / span) * h;
                return (
                  <g key={p.period}>
                    <circle cx={x} cy={y} r="4" fill="#378ADD" stroke="#0A0A0B" strokeWidth="2" />
                    <text x={x} y={h + 16} fill="#A1A1AA" fontSize="10" textAnchor={i === 0 ? "start" : i === hist.length - 1 ? "end" : "middle"}>
                      {p.period}
                    </text>
                    <text x={x} y={y - 8} fill="#fff" fontSize="11" fontWeight="500" textAnchor="middle">
                      {p.score}
                    </text>
                  </g>
                );
              })}
            </svg>
            <div className="flex-1 text-sm text-zinc-300">
              <p>
                <span className="text-white font-medium">{t("saas.score.trend_up")}</span>
                {t("saas.score.trend_text_a")}
                <span className="text-[#1D9E75] font-medium">{strongestLabel}</span>
                {t("saas.score.trend_text_b")}
                <span className="text-[#E24B4A] font-medium">{weakestLabel}</span>.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* PILLARS */}
      <div>
        <div className="flex items-end justify-between mb-3 flex-wrap gap-2">
          <div>
            <div className="noam-overline">{t("saas.score.pillars_overline")}</div>
            <div className="noam-display text-base font-semibold">{t("saas.score.pillars_title")}</div>
          </div>
          <div className="flex items-center gap-3">
            {data.is_partial && (
              <span
                className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-[#EF9F27] bg-[#EF9F27]/10 border border-[#EF9F27]/30 rounded px-2 py-1"
                title={t("saas.score.partial_hint", {
                  defaultValue: "Certains piliers ne sont pas alimentés. La note globale est calculée uniquement sur les piliers configurés (poids re-pondérés).",
                })}
                data-testid="score-partial-badge"
              >
                <Info className="w-3 h-3" />
                {t("saas.score.partial_label", {
                  count: data.pillars_configured_count,
                  total: data.pillars_total_count,
                  defaultValue: `Score basé sur ${data.pillars_configured_count}/${data.pillars_total_count} piliers`,
                })}
              </span>
            )}
            <span className="text-[11px] text-zinc-500">{t("saas.score.pillars_weight_note")}</span>
          </div>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-3">
          {data.pillars.map((p) => {
            const Icon = PILLAR_ICONS[p.key] || ShieldAlert;
            const isUnconfigured = p.configured === false || p.score === null;
            const c = isUnconfigured ? "#71717A" : pillarColor(p.score);
            const realtime = !isUnconfigured && p.realtime === true;
            return (
              <div
                key={p.key}
                className={`noam-card p-5 relative ${isUnconfigured ? "opacity-70 border-dashed" : ""}`}
                data-testid={`pillar-${p.key}`}
                data-configured={p.configured === false ? "false" : "true"}
              >
                <div className="flex items-center justify-between">
                  <Icon className="w-4 h-4" style={{ color: c }} />
                  <div className="flex items-center gap-1.5">
                    {realtime && (
                      <span
                        className="inline-flex items-center gap-1 text-[9px] uppercase tracking-wider text-[#1D9E75] bg-[#1D9E75]/10 border border-[#1D9E75]/30 rounded px-1.5 py-0.5 cursor-help"
                        title={p.key === "conformite"
                          ? t("saas.score.realtime_conformite", { defaultValue: "Calculé en temps réel depuis la collection « Contrôles » (statut actif + efficacité ≥70)." })
                          : p.key === "finance"
                            ? t("saas.score.realtime_finance", { defaultValue: "Calculé en temps réel depuis « Financement / Actifs » : score = 100 − LTV pondéré × 60." })
                            : t("saas.score.realtime_rh", { defaultValue: "Calculé en temps réel depuis « RH décisionnel » : taux de couverture des postes critiques avec owner + succession." })}
                        data-testid={`pillar-${p.key}-realtime-badge`}
                      >
                        <span className="w-1 h-1 rounded-full bg-[#1D9E75] animate-pulse" />
                        {t("saas.score.realtime", { defaultValue: "Temps réel" })}
                      </span>
                    )}
                    <span className="text-[10px] text-zinc-500 uppercase tracking-wider">{p.weight}%</span>
                  </div>
                </div>
                <div className="text-xs text-zinc-400 mt-3">{tPillar(p, "label", p.label)}</div>

                {isUnconfigured ? (
                  <>
                    <div className="flex items-baseline gap-1 mt-1">
                      <span className="noam-display text-2xl font-bold text-zinc-500" data-testid={`pillar-${p.key}-na`}>N/A</span>
                      <span className="text-xs text-zinc-600">{t("saas.score.not_configured", { defaultValue: "non configuré" })}</span>
                    </div>
                    <div className="h-1.5 mt-3 rounded-full bg-white/5 overflow-hidden">
                      <div className="h-full bg-zinc-700/40" style={{ width: "100%", backgroundImage: "repeating-linear-gradient(45deg, rgba(255,255,255,0.05) 0 4px, transparent 4px 8px)" }} />
                    </div>
                    <div className="mt-3 text-[11px] text-zinc-500 leading-relaxed">
                      {p.setup_hint || t("saas.score.configure_hint", { defaultValue: "Aucune donnée réelle dans ce pilier." })}
                    </div>
                    {p.setup_route && (
                      <Link
                        to={p.setup_route}
                        className="mt-3 inline-flex items-center gap-1 text-[11px] text-[#378ADD] hover:text-[#5BA4E8] hover:underline"
                        data-testid={`pillar-${p.key}-configure-cta`}
                      >
                        <Plus className="w-3 h-3" />
                        {t("saas.score.configure_cta", { defaultValue: "Configurer ce module" })}
                      </Link>
                    )}
                  </>
                ) : (
                  <>
                    <div className="flex items-baseline gap-1 mt-1">
                      <span className="noam-display text-3xl font-bold" style={{ color: c }}>{p.score}</span>
                      <span className="text-xs text-zinc-500">/100</span>
                    </div>
                    <div className="h-1.5 mt-3 rounded-full bg-white/5 overflow-hidden">
                      <div className="h-full transition-all" style={{ width: `${p.score}%`, background: c }} />
                    </div>
                    <div className="mt-3 text-[11px] text-zinc-500 leading-relaxed">{tPillar(p, "description", p.description)}</div>
                    <div className="mt-3 flex flex-wrap gap-1">
                      {p.indicateurs.slice(0, 3).map((ind) => (
                        <span key={ind} className="text-[10px] px-1.5 py-0.5 rounded border border-white/10 text-zinc-400">
                          {tIndicator(ind)}
                        </span>
                      ))}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* AI COMMENTARY */}
      <div className="noam-card p-6" data-testid="score-ai-commentary">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-[#378ADD]" />
            <div>
              <div className="noam-overline">{t("saas.score.ai_overline")}</div>
              <div className="noam-display text-base font-semibold">{t("saas.score.ai_title")}</div>
            </div>
          </div>
          <button
            onClick={generateCommentary}
            disabled={loadingCommentary}
            className="btn-primary text-xs disabled:opacity-50"
            data-testid="score-generate-commentary"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loadingCommentary ? "animate-spin" : ""}`} />
            {commentary ? t("saas.score.ai_regenerate") : t("saas.score.ai_generate")}
          </button>
        </div>

        {!commentary && !loadingCommentary && (
          <p className="text-zinc-400 text-sm">
            {t("saas.score.ai_intro")}
          </p>
        )}

        {loadingCommentary && (
          <div className="flex items-center gap-2 text-zinc-400 text-sm">
            <span className="typing-dot" /><span className="typing-dot" /><span className="typing-dot" />
            <span className="ml-2">{t("saas.score.ai_loading")}</span>
          </div>
        )}

        {commentary && (
          <>
            <p className="text-zinc-200 leading-relaxed border-l-2 border-[#378ADD] pl-4 italic" data-testid="score-commentary-text">
              {commentary}
            </p>
            <div className="mt-4">
              <CopyButton text={commentary} label={t("saas.score.copy_commentary")} />
            </div>
          </>
        )}
      </div>

      <div className="flex items-center justify-between flex-wrap gap-3 pt-2">
        <div className="text-xs text-zinc-500">
          {t("saas.score.method_note")}
        </div>
        <Link to="/app/executive" className="btn-ghost text-xs" data-testid="score-back-exec">
          {t("saas.score.back_executive")}
        </Link>
      </div>
    </div>
  );
}

function labelFor(pillars, key, t) {
  const p = pillars.find((x) => x.key === key);
  if (!p) return key;
  return t(`saas.pillars.${p.key}.label`, { defaultValue: p.label });
}
